import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import '../styles/Footer.css';

// استيراد مكونات Material UI
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';

// استيراد أيقونات Material UI
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import PolicyIcon from '@mui/icons-material/Policy';

const Footer = () => {
  const { theme } = useTheme();
  const { translations } = useLanguage();
  
  // قائمة التخصصات الطبية الشائعة
  const popularSpecialties = [
    'طب الأسنان',
    'طب العيون',
    'أمراض القلب',
    'طب الأطفال',
    'الأمراض الجلدية',
    'الجراحة العامة',
    'النساء والتوليد',
    'العظام'
  ];
  
  // روابط سريعة
  const quickLinks = [
    { title: translations?.general?.home || 'الرئيسية', path: '/' },
    { title: translations?.general?.clinics || 'العيادات', path: '/clinics' },
    { title: translations?.general?.doctors || 'الأطباء', path: '/doctors' },
    { title: translations?.general?.appointments || 'المواعيد', path: '/appointments' },
    { title: translations?.general?.about || 'من نحن', path: '/about' },
    { title: translations?.general?.contact || 'اتصل بنا', path: '/contact' },
  ];
  
  // روابط المساعدة
  const helpLinks = [
    { title: translations?.help?.faq || 'الأسئلة الشائعة', path: '/faq', icon: <HelpIcon fontSize="small" /> },
    { title: translations?.help?.howToUse || 'كيفية الاستخدام', path: '/how-to-use', icon: <InfoIcon fontSize="small" /> },
    { title: translations?.help?.terms || 'الشروط والأحكام', path: '/terms', icon: <PolicyIcon fontSize="small" /> },
    { title: translations?.help?.privacy || 'سياسة الخصوصية', path: '/privacy', icon: <PolicyIcon fontSize="small" /> },
  ];
  
  return (
    <footer className={`footer ${theme}`}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* معلومات عن التطبيق */}
          <Grid item xs={12} sm={6} md={3}>
            <Box className="footer-brand">
              <Link to="/" className="footer-logo-container">
                <img src="/new-logo.svg" alt="Doctor Libya Logo" className="footer-logo" />
                <Typography variant="h6" className="footer-title">
                  Doctor Libya
                </Typography>
              </Link>
              <Typography variant="body2" className="footer-description">
                {translations?.footer?.description || 
                  'منصة طبية متكاملة تربط المرضى بالأطباء والعيادات في ليبيا، وتسهل حجز المواعيد ومتابعة الحالات الصحية.'}
              </Typography>
              <Box className="social-icons">
                <IconButton aria-label="facebook" className="social-icon">
                  <FacebookIcon />
                </IconButton>
                <IconButton aria-label="twitter" className="social-icon">
                  <TwitterIcon />
                </IconButton>
                <IconButton aria-label="instagram" className="social-icon">
                  <InstagramIcon />
                </IconButton>
                <IconButton aria-label="linkedin" className="social-icon">
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>
          
          {/* روابط سريعة */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className="footer-title">
              {translations?.footer?.quickLinks || 'روابط سريعة'}
            </Typography>
            <List className="footer-links">
              {quickLinks.map((link, index) => (
                <ListItem key={index} component={Link} to={link.path} className="footer-link-item">
                  <ArrowForwardIosIcon fontSize="small" className="link-arrow" />
                  <ListItemText primary={link.title} />
                </ListItem>
              ))}
            </List>
          </Grid>
          
          {/* التخصصات الشائعة */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className="footer-title">
              {translations?.footer?.popularSpecialties || 'التخصصات الشائعة'}
            </Typography>
            <List className="footer-links specialty-links">
              {popularSpecialties.map((specialty, index) => (
                <ListItem key={index} component={Link} to={`/specialty/${specialty}`} className="footer-link-item">
                  <LocalHospitalIcon fontSize="small" className="specialty-icon" />
                  <ListItemText primary={specialty} />
                </ListItem>
              ))}
            </List>
          </Grid>
          
          {/* معلومات الاتصال والمساعدة */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className="footer-title">
              {translations?.footer?.contactUs || 'اتصل بنا'}
            </Typography>
            <List className="contact-info">
              <ListItem className="contact-item">
                <ListItemIcon className="contact-icon">
                  <PhoneIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="+218 91-234-5678" />
              </ListItem>
              <ListItem className="contact-item">
                <ListItemIcon className="contact-icon">
                  <EmailIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="info@doctorlibya.com" />
              </ListItem>
              <ListItem className="contact-item">
                <ListItemIcon className="contact-icon">
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="طرابلس، ليبيا" />
              </ListItem>
            </List>
            
            <Typography variant="h6" className="footer-title help-title">
              {translations?.footer?.help || 'المساعدة'}
            </Typography>
            <List className="footer-links help-links">
              {helpLinks.map((link, index) => (
                <ListItem key={index} component={Link} to={link.path} className="footer-link-item">
                  {link.icon}
                  <ListItemText primary={link.title} />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
        
        <Divider className="footer-divider" />
        
        <Box className="footer-bottom">
          <Typography variant="body2" className="copyright">
            &copy; {new Date().getFullYear()} Doctor Libya. {translations?.footer?.allRightsReserved || 'جميع الحقوق محفوظة.'}
          </Typography>
          <Box className="footer-bottom-links">
            <Button component={Link} to="/terms" color="inherit" size="small">
              {translations?.help?.terms || 'الشروط والأحكام'}
            </Button>
            <Button component={Link} to="/privacy" color="inherit" size="small">
              {translations?.help?.privacy || 'سياسة الخصوصية'}
            </Button>
          </Box>
        </Box>
      </Container>
    </footer>
  );
};

export default Footer;